import OutgoingRequest from "@mpa/toolbox/endpoint/OutgoingRequest";
import type { BriefSerialized } from "@mpa/toolbox/models/Brief";
import Candidate from "@mpa/toolbox/models/Candidate";
import Contact from "@mpa/toolbox/models/Contact";
import type { SendEmail } from "@mpa/toolbox/models/Email";
import JoinSession from "@mpa/toolbox/models/JoinSession";
import { User } from "@mpa/toolbox/models/User";

import { notifyLoggedInChanged } from "../data/loggedIn";
import { deleteOutgoingRequest, queryClient, refreshOutgoingRequest } from "../data/tanstack-query";
import type { Permission } from "../enums/Permission";
import { api } from "./endpoints";

export const requestEmailCode = (body: { email: string }) => {
    return new OutgoingRequest(api.iam.endpoints.emailCodeEndpoint).setBody(body);
};

export const confirmEmailCode = (body: { email: string; code: string }) => {
    return new OutgoingRequest(api.iam.endpoints.emailCodeConfirmEndpoint)
        .setBody(body)
        .onSuccess(() => notifyLoggedInChanged());
};

export const createAnonymousId = () => {
    return new OutgoingRequest(api.iam.endpoints.anonymousIdsEndpoint);
};

export const signupAnonymous = (body: { id: string; password: string }) => {
    return new OutgoingRequest(api.iam.endpoints.anonymousSignupEndpoint).setBody(body);
};

export const loginAnonymous = (body: { id: string; password: string }) => {
    return new OutgoingRequest(api.iam.endpoints.anonymousLoginEndpoint)
        .setBody(body)
        .onSuccess(() => notifyLoggedInChanged());
};

export const logout = () => {
    return new OutgoingRequest(api.iam.endpoints.logoutEndpoint).onSuccess(() => {
        queryClient.clear();
        notifyLoggedInChanged();
    });
};

export const getMe = () => {
    return new OutgoingRequest(api.authenticated.endpoints.getMe).setDeserializer((r) =>
        User.deserialize(r),
    );
};

export const getUsers = () => {
    return new OutgoingRequest(api.user.endpoints.getUsersEndpoint);
};
export const getUser = (id: string) => {
    return new OutgoingRequest(api.user.endpoints.getUserEndpoint).setPathParams({ user: id });
};
export const deleteUser = (id: string, self = false) => {
    return new OutgoingRequest(api.user.endpoints.deleteUserEndpoint)
        .setPathParams({ user: id })
        .onSuccess(() => {
            if (self) {
                queryClient.clear();
                notifyLoggedInChanged();
                return;
            }
            refreshOutgoingRequest(getUsers());
            deleteOutgoingRequest(getUser(id));
        });
};
export const updateUser = (id: string, permissions: Permission[]) => {
    return new OutgoingRequest(api.user.endpoints.updateUserEndpoint)
        .setPathParams({ user: id })
        .setBody({ permissions })
        .onSuccess(() => {
            refreshOutgoingRequest(getUsers());
            refreshOutgoingRequest(getUser(id));
            refreshOutgoingRequest(getMe());
        });
};
export const invalidateLogin = (userId: string, loginId: string, current = false) => {
    return new OutgoingRequest(api.user.endpoints.invalidateUserLoginEndpoint)
        .setPathParams({ user: userId, login: loginId })
        .onSuccess(() => {
            if (current) {
                queryClient.clear();
                notifyLoggedInChanged();
                return;
            }
            refreshOutgoingRequest(getUser(userId));
        });
};
export const invalidateUserLogins = (userId: string, includesCurrent = false) => {
    return new OutgoingRequest(api.user.endpoints.invalidateUserLoginsEndpoint)
        .setPathParams({ user: userId })
        .onSuccess(() => {
            if (includesCurrent) {
                queryClient.clear();
                notifyLoggedInChanged();
                return;
            }
            refreshOutgoingRequest(getUser(userId));
        });
};
export const deleteUserProject = (userId: string, projetId: string) => {
    return new OutgoingRequest(api.user.endpoints.deleteUserProjectEndpoint)
        .setPathParams({ user: userId, projet: projetId })
        .onSuccess(() => {
            refreshOutgoingRequest(getUser(userId));
            refreshOutgoingRequest(getProjects());
            refreshOutgoingRequest(getCandidates());
        });
};
export const detachUserProject = (userId: string, projetId: string) => {
    return new OutgoingRequest(api.user.endpoints.detachUserProjectEndpoint)
        .setPathParams({ user: userId, projet: projetId })
        .onSuccess(() => {
            refreshOutgoingRequest(getUser(userId));
            refreshOutgoingRequest(getProjects());
            refreshOutgoingRequest(getCandidates());
        });
};

export const getCurrentProject = () => {
    return new OutgoingRequest(api.authenticated.endpoints.getCurrentProject);
};
export const getProjects = () => {
    return new OutgoingRequest(api.authenticated.endpoints.getProjects);
};
export const createProject = () => {
    return new OutgoingRequest(api.authenticated.endpoints.createProject)
        .setBody({})
        .onSuccess(() => refreshOutgoingRequest(getProjects()));
};
export const joinProjet = (code: string) => {
    return new OutgoingRequest(api.authenticated.endpoints.joinProject)
        .setBody({ code })
        .onSuccess(() => refreshOutgoingRequest(getProjects()));
};
export const createJoinSession = () => {
    return new OutgoingRequest(api.joinSession.endpoints.createJoinSessionEndpoint)
        .setBody({})
        .setDeserializer((joinSession) => JoinSession.deserialize(joinSession));
};
export const getJoinSession = (id: string) => {
    return new OutgoingRequest(api.joinSession.endpoints.getJoinSessionEndpoint)
        .setPathParams({ joinSession: id })
        .setDeserializer((joinSession) => JoinSession.deserialize(joinSession));
};
export const invalidateJoinSession = (id: string) => {
    return new OutgoingRequest(api.joinSession.endpoints.invalidateJoinSessionEndpoint)
        .setPathParams({ joinSession: id })
        .setBody({})
        .setDeserializer((joinSession) => JoinSession.deserialize(joinSession));
};
export const deleteProject = (user: string, project: string) => {
    return new OutgoingRequest(api.user.endpoints.deleteUserProjectEndpoint).setPathParams({
        user,
        projet: project,
    });
};
export const updateProjet = (label: string) => {
    return new OutgoingRequest(api.project.endpoints.updateProjetEndpoint)
        .setBody({ label })
        .onSuccess(() => {
            refreshOutgoingRequest(getProjects());
            refreshOutgoingRequest(getCurrentProject());
        });
};

export const getCandidates = () => {
    return new OutgoingRequest(api.candidate.endpoints.getCandidatesEndpoint).setDeserializer((r) =>
        r.map((r) => Candidate.deserialize(r)),
    );
};
export const getCandidate = (id: string) => {
    return new OutgoingRequest(api.candidate.endpoints.getCandidateEndpoint)
        .setPathParams({ user: id })
        .setDeserializer((r) => Candidate.deserialize(r));
};
export const updateCandidate = (id: string, candidate: Candidate) => {
    return new OutgoingRequest(api.candidate.endpoints.updateCandidateEndpoint)
        .setPathParams({ user: id })
        .setBody(candidate.serialize())
        .onSuccess(() => {
            refreshOutgoingRequest(getCandidates());
            refreshOutgoingRequest(getCandidate(id));
        });
};

export const getContacts = () => {
    return new OutgoingRequest(api.contact.endpoints.getContactsEndpoint).setDeserializer((r) =>
        r.map((r) => Contact.deserialize(r)),
    );
};
export const getContact = (id: string) => {
    return new OutgoingRequest(api.contact.endpoints.getContactEndpoint)
        .setPathParams({ contact: id })
        .setDeserializer((r) => Contact.deserialize(r));
};
export const createContact = (contact: Contact) => {
    return new OutgoingRequest(api.contact.endpoints.createContactsEndpoint)
        .setBody(contact.serialize())
        .onSuccess(() => refreshOutgoingRequest(getContacts()));
};
export const updateContact = (id: string, contact: Contact) => {
    return new OutgoingRequest(api.contact.endpoints.updateContactEndpoint)
        .setPathParams({ contact: id })
        .setBody(contact.serialize())
        .onSuccess(() => {
            refreshOutgoingRequest(getContacts());
            refreshOutgoingRequest(getContact(id));
        });
};
export const deleteContact = (id: string) => {
    return new OutgoingRequest(api.contact.endpoints.deleteContactEndpoint)
        .setPathParams({ contact: id })
        .onSuccess(() => {
            refreshOutgoingRequest(getContacts());
            deleteOutgoingRequest(getContact(id));
        });
};

export const getBrief = (id: string) => {
    return new OutgoingRequest(api.brief.endpoints.getBriefEndpoint).setPathParams({ brief: id });
};
export const updateBrief = (id: string, brief: BriefSerialized) => {
    return new OutgoingRequest(api.brief.endpoints.updateBriefEndpoint)
        .setPathParams({ brief: id })
        .setBody(brief)
        .onSuccess(() => {
            refreshOutgoingRequest(getBrief(id));
        });
};

export const getEmails = () => {
    return new OutgoingRequest(api.mailing.endpoints.getEmailsEndpoint);
};
export const getEmail = (id: string) => {
    return new OutgoingRequest(api.mailing.endpoints.getEmailEndpoint).setPathParams({ email: id });
};
export const searchEmailRecipients = (search: string) => {
    return new OutgoingRequest(api.mailing.endpoints.searchEmailRecipientsEndpoint).setQueryParams({
        search,
    });
};
export const sendEmail = (email: SendEmail) => {
    return new OutgoingRequest(api.mailing.endpoints.sendEmailEndpoint)
        .setBody(email)
        .onSuccess(() => refreshOutgoingRequest(getEmails()));
};
